// Reusable "Edit partner" modal — mirrors the staff/super-admin edit form on
// the Partners page, so the same fields can be edited from the per-partner
// commissions detail page. Self-contained: loads its own meta (partner types +
// territories) and PUTs to /organizations/{orgId}.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { CA_TAX_PRESETS } from "@/pages/Partners";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const EMPTY = {
  name: "", type: "partner", partner_type: "none", status: "active",
  contact_name: "", contact_email: "", contact_phone: "", notes: "",
  commission_alias: "", tax_rate: "", tax_jurisdiction: "", tax_number: "", territory: "",
};

export function PartnerEditDialog({ orgId, initialOrg, open, onOpenChange, onSaved }) {
  const [partnerTypes, setPartnerTypes] = useState([]);
  const [territories, setTerritories] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [terrCustom, setTerrCustom] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/meta").then((r) => {
      setPartnerTypes(r.data.partner_types || []);
      setTerritories(r.data.territories || []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (open && initialOrg) {
      setForm({ ...EMPTY, ...initialOrg, type: "partner",
                partner_type: initialOrg.partner_type || "none",
                status: initialOrg.status || "active",
                tax_rate: initialOrg.tax_rate ?? "" });
      setTerrCustom(false);
    }
  }, [open, initialOrg]);

  const save = async () => {
    if (!(form.name || "").trim()) { toast.error("Name is required."); return; }
    const taxRateNum = form.tax_rate === "" || form.tax_rate === null || form.tax_rate === undefined
      ? null : Number(form.tax_rate);
    const payload = {
      ...form, type: "partner", parent_org_id: null,
      partner_type: form.partner_type !== "none" ? form.partner_type : null,
      tax_rate: Number.isFinite(taxRateNum) ? taxRateNum : null,
    };
    if (!(payload.territory || "").trim()) {
      toast.error("Territory is required for partner organizations."); return;
    }
    payload.territory = (payload.territory || "").trim();
    setSaving(true);
    try {
      await api.put(`/organizations/${orgId}`, payload);
      toast.success("Partner updated");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const currentVal = (form.territory || "").trim();
  const inList = currentVal && territories.includes(currentVal);
  const customActive = terrCustom || (currentVal && !inList);
  const terrSelectValue = customActive ? "__custom__" : (currentVal || "");

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!saving) onOpenChange(o); }}>
      <DialogContent className="flex max-h-[85vh] max-w-md flex-col gap-0 p-0" data-testid="partner-edit-dialog">
        <DialogHeader className="border-b border-border px-6 py-4">
          <DialogTitle>Edit partner</DialogTitle>
        </DialogHeader>
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-6 py-4" data-testid="partner-edit-scroll">
          <div className="space-y-1.5"><Label>Name</Label>
            <Input data-testid="partner-name-input" value={form.name}
                   onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Partner type</Label>
              <Select value={form.partner_type} onValueChange={(v) => setForm({ ...form, partner_type: v })}>
                <SelectTrigger data-testid="partner-type-select"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unspecified</SelectItem>
                  {partnerTypes.map((pt) => <SelectItem key={pt} value={pt} data-testid={`partner-type-${pt.replace(/\s+/g, '-').toLowerCase()}`}>{pt}</SelectItem>)}
                </SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="partner-status-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="suspended">Suspended</SelectItem><SelectItem value="deactivated">Deactivated</SelectItem></SelectContent>
              </Select></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Contact name</Label><Input data-testid="partner-contact-name" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Contact phone</Label><Input data-testid="partner-contact-phone" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} /></div>
          </div>
          <div className="space-y-1.5"><Label>Contact email</Label><Input data-testid="partner-contact-email" type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} /></div>
          <div className="space-y-1.5" data-testid="partner-territory-row">
            <Label>Territory <span className="text-red-500">*</span></Label>
            <Select value={terrSelectValue}
                    onValueChange={(v) => {
                      if (v === "__custom__") { setTerrCustom(true); setForm({ ...form, territory: "" }); }
                      else { setTerrCustom(false); setForm({ ...form, territory: v }); }
                    }}>
              <SelectTrigger data-testid="partner-territory-select"><SelectValue placeholder="Pick a territory…" /></SelectTrigger>
              <SelectContent>
                {territories.map((t) => <SelectItem key={t} value={t} data-testid={`partner-territory-opt-${t}`}>{t}</SelectItem>)}
                <SelectItem value="__custom__" data-testid="partner-territory-custom">Custom / Other…</SelectItem>
              </SelectContent>
            </Select>
            {customActive && (
              <Input data-testid="partner-territory-input" placeholder="e.g. ngenuity.ca"
                     value={form.territory || ""} onChange={(e) => setForm({ ...form, territory: e.target.value })} />
            )}
            <div className="text-[11px] text-slate-400">
              Scopes the Audit Log to rows where <code className="font-mono">territory</code> equals this value.
              Every user assigned to this partner inherits it — edit once here to retarget everyone.
              Missing an option? Super-admins can add it under Settings → Marketing → Territories.
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Commission alias <span className="text-xs font-normal text-slate-400">(optional)</span></Label>
            <Input data-testid="partner-commission-alias" value={form.commission_alias || ""}
                   placeholder={form.name ? `Defaults to "${form.name}"` : "e.g. Kobelt Development"}
                   onChange={(e) => setForm({ ...form, commission_alias: e.target.value })} />
            <div className="text-[11px] text-slate-400">Override how this partner is matched in the Stax commission export.</div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label>Jurisdiction</Label>
              <Select
                value={CA_TAX_PRESETS.find((p) => p.value === form.tax_jurisdiction)?.code || (form.tax_jurisdiction ? "OTHER" : "NONE")}
                onValueChange={(code) => {
                  if (code === "NONE") setForm({ ...form, tax_jurisdiction: "", tax_rate: "" });
                  else if (code === "OTHER") setForm({ ...form, tax_jurisdiction: form.tax_jurisdiction && !CA_TAX_PRESETS.some(p => p.value === form.tax_jurisdiction) ? form.tax_jurisdiction : "" });
                  else { const preset = CA_TAX_PRESETS.find((p) => p.code === code); if (preset) setForm({ ...form, tax_jurisdiction: preset.value, tax_rate: preset.rate }); }
                }}>
                <SelectTrigger data-testid="partner-tax-jurisdiction-select"><SelectValue placeholder="Choose a province…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="NONE">— None (not taxable) —</SelectItem>
                  {CA_TAX_PRESETS.map((p) => <SelectItem key={p.code} value={p.code} data-testid={`partner-tax-jur-${p.code}`}>{p.label}</SelectItem>)}
                  <SelectItem value="OTHER">Custom / Other</SelectItem>
                </SelectContent>
              </Select>
              {(!CA_TAX_PRESETS.some((p) => p.value === form.tax_jurisdiction) && form.tax_jurisdiction !== "") && (
                <Input data-testid="partner-tax-jurisdiction" value={form.tax_jurisdiction || ""} placeholder="e.g. NY · Sales Tax"
                       onChange={(e) => setForm({ ...form, tax_jurisdiction: e.target.value })} />
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Tax rate %</Label>
              <Input data-testid="partner-tax-rate" type="number" step="0.001" value={form.tax_rate ?? ""} placeholder="5.00"
                     onChange={(e) => setForm({ ...form, tax_rate: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Tax registration #</Label>
              <Input data-testid="partner-tax-number" value={form.tax_number || ""} placeholder="Optional"
                     onChange={(e) => setForm({ ...form, tax_number: e.target.value })} />
            </div>
          </div>
          <div className="text-[11px] text-slate-400 -mt-2">Used to generate pro-forma commission invoices with the correct sales tax line.</div>
          <div className="space-y-1.5"><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} /></div>
        </div>
        <DialogFooter className="border-t border-border px-6 py-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button data-testid="save-partner-button" onClick={save} disabled={saving} className="bg-primary hover:bg-primary/90 text-primary-foreground">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
